<template>
  <section v-if="!isReady" class="pipeline-history">
    <p-loading />
  </section>

  <section v-else class="pipeline-history">
    <div class="header-wrapper">
      <Button variant="text" @click="$router.push(`/pipelines/${$route.params.id}`)">
        <MdIcon name="arrow-left" size="sm" /> Back to pipeline
      </Button>
      <div class="total"><label>Total:</label> {{ history.total }}</div>
    </div>
    <div class="filters">
      <TextField v-model="applicationId" label="Application ID" placeholder="Search" />
      <div class="status-filter">
        <label>Status</label>
        <Dropdown :value="status" :options="statusOptions" @change="onStatusChange" />
      </div>
      <DateRange :value="dateRange" label="Started" @input="onDateRangeChange" />
    </div>
    <ul class="history-list">
      <li class="history-list-item header">
        <div>Application ID</div>
        <div>Version</div>
        <div>Status</div>
        <div>Started At</div>
        <div>Completed At</div>
        <div>Log</div>
      </li>
    </ul>
    <ul class="history-list">
      <li v-for="entry in history.data" :key="entry._id" class="history-list-item">
        <div>{{ entry.applicationId }}</div>
        <div>{{ entry.version }}</div>
        <div>
          <span class="status-label" :class="statusClass(entry.status)">{{ entry.status }}</span>
        </div>
        <div>{{ entry.startedAt ? new Date(entry.startedAt).toLocaleString() : '' }}</div>
        <div>{{ entry.completedAt ? new Date(entry.completedAt).toLocaleString() : '' }}</div>
        <div>
          <p-button v-if="entry.message" variant="text" color="secondary" title="Inspect this run" @click="inspect(entry)">
            <MdIcon name="magnify" size="sm" />
          </p-button>
        </div>
      </li>
      <li v-if="!history.data.length" class="history-list-item empty">No runs yet.</li>
    </ul>
    <Pagination :total="history.total" :size="HISTORY_PAGE_SIZE" :from="from" @navigate="onNavigate" />
    <inspect-modal v-if="inspectModalVisible" :message="itemToInspect.message" @close="inspectModalVisible = false" />
  </section>
</template>

<script>
import { mapState } from 'vuex';
import Button from '@/components/common/Button.vue';
import DateRange from '@/components/common/DateRange.vue';
import Dropdown from '@/components/common/Dropdown.vue';
import Loading from '@/components/common/Loading.vue';
import MdIcon from '@/components/common/MdIcon.vue';
import Pagination from '@/components/common/Pagination.vue';
import TextField from '@/components/common/TextField.vue';
import InspectModal from './InspectModal.vue';

const HISTORY_PAGE_SIZE = 20;
const APPLICATION_ID_DEBOUNCE_MS = 300;

const STATUS_OPTIONS = [
  { id: '', label: 'All' },
  { id: 'completed', label: 'Completed' },
  { id: 'partially_completed', label: 'Partially completed' },
  { id: 'failed', label: 'Failed' }
];

function mmddyyyyToIso(raw, endOfDay) {
  if (!raw || String(raw).length !== 8) {
    return undefined;
  }
  const month = Number(raw.slice(0, 2));
  const day = Number(raw.slice(2, 4));
  const year = Number(raw.slice(4, 8));
  const date = new Date(year, month - 1, day);
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }
  if (endOfDay) {
    date.setHours(23, 59, 59, 999);
  } else {
    date.setHours(0, 0, 0, 0);
  }
  return date.toISOString();
}

export default {
  name: 'PipelineRunHistory',
  components: {
    'p-button': Button,
    'p-loading': Loading,
    'inspect-modal': InspectModal,
    Button,
    DateRange,
    Dropdown,
    MdIcon,
    Pagination,
    TextField
  },
  beforeRouteUpdate(to, from, next) {
    this.loadPage(0, to.params.id).then(() => next());
  },
  data() {
    return {
      HISTORY_PAGE_SIZE,
      isReady: false,
      inspectModalVisible: false,
      itemToInspect: null,
      from: 0,
      applicationId: '',
      status: '',
      dateRange: { from: undefined, to: undefined },
      statusOptions: STATUS_OPTIONS,
      applicationIdDebounce: null
    };
  },
  computed: {
    ...mapState('pipelines', ['history'])
  },
  watch: {
    applicationId() {
      clearTimeout(this.applicationIdDebounce);
      this.applicationIdDebounce = setTimeout(() => this.loadPage(0), APPLICATION_ID_DEBOUNCE_MS);
    }
  },
  async created() {
    await this.loadPage(0);
  },
  beforeDestroy() {
    clearTimeout(this.applicationIdDebounce);
    this.$store.commit('pipelines/RESET_HISTORY');
  },
  methods: {
    inspect(item) {
      this.inspectModalVisible = true;
      this.itemToInspect = item;
    },
    statusClass(status) {
      switch ((status || '').toLowerCase()) {
        case 'completed':
          return 'completed';
        case 'partially_completed':
          return 'partially-completed';
        case 'failed':
          return 'failed';
        default:
          return 'pending';
      }
    },
    onStatusChange(status) {
      this.status = status;
      this.loadPage(0);
    },
    onDateRangeChange(dateRange) {
      this.dateRange = {
        from: dateRange && dateRange.from,
        to: dateRange && dateRange.to
      };
      this.loadPage(0);
    },
    onNavigate({ from }) {
      this.loadPage(from);
    },
    async loadPage(from, pipelineId = this.$route.params.id) {
      this.from = from;
      const startedFrom = mmddyyyyToIso(this.dateRange.from, false);
      const startedTo = mmddyyyyToIso(this.dateRange.to, true);
      try {
        await this.$store.dispatch('pipelines/fetchHistory', {
          pipelineId,
          skip: from,
          limit: HISTORY_PAGE_SIZE,
          ...(this.status ? { status: this.status } : {}),
          ...(this.applicationId.trim() ? { applicationId: this.applicationId.trim() } : {}),
          ...(startedFrom ? { startedFrom } : {}),
          ...(startedTo ? { startedTo } : {})
        });
      } catch (e) {
        this.$toast.error({
          title: 'Failed to load history',
          message: 'Please, try again later or contact our development team.'
        });
      } finally {
        this.isReady = true;
      }
    }
  }
};
</script>
<style lang="scss" scoped>
.pipeline-history {
  .header-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.75rem;
    margin: 0 6px 0.5rem 0;
    padding: 0;
    font-weight: 400;

    .total {
      display: flex;
      align-items: center;
    }

    label {
      font-weight: 500;
      margin-right: 0.5rem;
    }
  }

  .filters {
    display: grid;
    grid-template-columns: minmax(8rem, 1fr) max-content minmax(12rem, 1.5fr);
    grid-gap: 0.75rem;
    align-items: end;
    margin: 0 6px 0.75rem 0;

    .status-filter {
      display: grid;
      grid-template-rows: max-content max-content;
      grid-gap: 5px;

      label {
        font-weight: 500;
        font-size: 0.75rem;
        letter-spacing: 0.025em;
      }

      // match the height and framing of the sibling text/date inputs
      .dropdown {
        min-width: 11rem;
        min-height: 35px;
        padding: 0 12px;
        align-items: center;
        justify-content: space-between;
        border: 1px solid var(--theme-on-background-dark);
        border-radius: 2px;
        background: var(--theme-background);
      }
    }
  }

  .history-list {
    margin: 0;
    padding: 0;
    list-style: none;

    .history-list-item {
      display: grid;
      grid-template-columns: minmax(0, 2fr) 4.5rem 10rem minmax(0, 1.75fr) minmax(0, 1.75fr) 3rem;
      grid-gap: 0;
      border-bottom: 1px solid var(--theme-highlight);
      font-size: 0.75rem;
      align-items: center;

      label {
        font-weight: 700;
      }

      > * {
        min-width: 0;
        padding: 0.35rem 0.5rem;
        border-left: 1px solid var(--theme-highlight);
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;

        &:last-child {
          border-right: 1px solid var(--theme-highlight);
        }
      }

      &:first-child {
        border-top: 1px solid var(--theme-highlight);
      }

      &.header {
        font-size: 0.75rem;
        color: var(--theme-on-background-accent);

        > * {
          padding: 0.25rem 0.5rem;
        }
      }

      &.empty {
        grid-template-columns: 1fr;
        color: var(--theme-on-background-accent);
      }
    }

    &:not(:last-child) {
      margin-right: 6px;
    }

    &:last-child {
      overflow-y: scroll;
    }
  }

  .status-label {
    display: inline-flex;
    line-height: 1;
    height: 1rem;
    justify-content: center;
    align-items: center;
    border-radius: 10%;
    padding: 0.2rem 0.4rem;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: uppercase;

    &.completed {
      color: var(--theme-on-success);
      background-color: var(--theme-success);
    }

    &.failed {
      color: var(--theme-on-error);
      background-color: var(--theme-error);
    }

    &.partially-completed {
      color: var(--theme-on-warning, var(--theme-on-secondary));
      background-color: var(--theme-warning);
    }

    &.pending {
      color: var(--theme-on-background-accent);
      background-color: var(--theme-highlight);
    }
  }
}
</style>
